import AppView from "../../../../app.view";
import Api from "../../../../services/api";
import { DriveStore } from "../../../../store/reducers";
import { getCurrentVehicle } from "../../../../utils/device";
import { InspectionLogService } from "../../../inspections/services/inspectionLogService";
import { getAssetTypeGroups, getRegulatoryAssetTypeIds } from "../../../inspections/services/assetTypeGroupsService";
import { NO_DEVICE_ID } from "../../../inspections/services/inspectionAssetsService";
import { createAssetTypeMap, getAssetTypeId, getInspectionLogTime } from "../../../inspections/utils/assetFormatters";
import { InspectionTemplateService } from "../../../inspections/services/inspectionTemplateService";
import { hasRegulatedItem, isRegulatoryAssetType } from "../../../inspections/utils/regulatoryInspection";
import { IInspectionAsset, IInspectionLog, IInspectionTemplateDetails } from "../../../inspections/store/types";

/**
 * Transforms the raw InspectionLog into the IInspectionAsset shape the Inspection History
 * Detail page reads from Redux (state.inspection.selectedHistoryAsset).
 */
async function buildHistoryAsset(
    inspectionLogService: InspectionLogService,
    assetTypeMap: Map<string, string>,
    inspectionLog: IInspectionLog
): Promise<IInspectionAsset | null> {
    try {
        const assets = await inspectionLogService.transformInspectionLogsToAssets([inspectionLog], assetTypeMap);
        return assets[0] ?? null;
    } catch {
        return null;
    }
}

/**
 * The newest log for this vehicle whose template holds a regulated item, or null when none of the
 * recent ones does.
 *
 * The item term of the history detail page's rule (MYG-153703); the asset-type term is settled by
 * the caller, since every log here is for the same vehicle. A template that cannot be read passes,
 * so a failed lookup never hides a DVIR from an officer.
 *
 * Every distinct template is read in one multiCall before the scan: any recent log may need its
 * template now that the inspection type no longer filters candidates, and reading them one at a
 * time would hold the roadside screen for a round trip per template.
 */
async function findLastRegulatoryLog(
    inspectionLogService: InspectionLogService,
    api: Api,
    vehicleId: string
): Promise<IInspectionLog | null> {
    const logs = await inspectionLogService.getRecentSubmittedLogsForDevice(vehicleId);
    const templateIds: string[] = [];
    logs.forEach(log => {
        const templateId = log.inspectionTemplate?.id;
        if (templateId && templateIds.indexOf(templateId) === -1) {
            templateIds.push(templateId);
        }
    });
    const templateById = await new InspectionTemplateService(api)
        .getTemplateDetailsByIds(templateIds)
        .catch(() => new Map<string, IInspectionTemplateDetails>());

    for (const log of logs) {
        const templateId = log.inspectionTemplate?.id;
        const template = templateId ? templateById.get(templateId) : undefined;
        if (!template || hasRegulatedItem(template)) {
            return log;
        }
    }

    return null;
}

/**
 * Resolves the vehicle's most recent *regulatory* inspection for a roadside check. Returns the
 * prepared history asset when that record is at least as recent as the vehicle's newest legacy
 * DVIR, null otherwise — so the caller falls back to the legacy DVIR path (MYG-141213).
 *
 * Non-regulatory inspections are skipped rather than disqualifying the vehicle: an officer asking
 * for the last DVIR must not be shown a checklist that is not one, and must not be told there is
 * nothing simply because the newest record happens to be non-regulatory (MYG-149798). A record is
 * regulated when the vehicle is a regulatory asset type and its template holds a regulated item
 * (MYG-153703).
 *
 * Vehicle-scoped on purpose (no user filter): during a roadside check the officer needs
 * the last completed inspection for this vehicle regardless of which driver performed it.
 */
export async function resolveLastRegulatoryInspection(
    app: AppView,
    api: Api,
    store: DriveStore
): Promise<IInspectionAsset | null> {
    const vehicle = getCurrentVehicle(store);
    if (!vehicle?.id || vehicle.id === NO_DEVICE_ID) {
        return null;
    }

    if (!app.securityService.can("viewAssetInspectionLogs")) {
        return null;
    }

    const inspectionLogService = new InspectionLogService(api);
    const regulatoryLog = await findLastRegulatoryLog(inspectionLogService, api, vehicle.id);

    if (!regulatoryLog) {
        return null;
    }

    // Redirect only when the regulatory record is the *last* completed inspection: if the
    // vehicle's newest legacy DVIR is more recent, the legacy path must show that DVIR instead.
    const latestDvir = app.dvirLogsService.getLatestLog(app.dvirLogsService.getDvirLogs(), vehicle.id);
    if (latestDvir?.dateTime && new Date(latestDvir.dateTime).getTime() > getInspectionLogTime(regulatoryLog)) {
        return null;
    }

    // Degrades to null rather than rejecting: the caller treats null as "use the legacy DVIR path",
    // and an unhandled rejection here would break the roadside screen instead of falling back.
    const assetTypes = await getAssetTypeGroups(api).catch(() => null);
    if (!assetTypes) {
        return null;
    }
    const assetTypeMap = createAssetTypeMap(assetTypes);
    const historyAsset = await buildHistoryAsset(inspectionLogService, assetTypeMap, regulatoryLog);
    if (!historyAsset) {
        return null;
    }

    // Asset-type term, checked here because getCurrentVehicle returns a bare {id} — only the built
    // history asset carries the hydrated device whose groups identify the asset type. It does not
    // vary per log, so one check after the scan is equivalent to one per candidate.
    const assetTypeId = getAssetTypeId(historyAsset, assetTypeMap);
    if (!isRegulatoryAssetType(assetTypeId, new Set(await getRegulatoryAssetTypeIds(api)))) {
        return null;
    }

    return historyAsset;
}
